/**
 * Teamwork API (api/app/collab/routes.py, docs/collab.md): internal notes, customer tags, follow-up sending,
 * the "การเตือนและทักตาม" settings, and the realtime events that go with them (presence, note/tag changes).
 */
import type { ConversationSummary, SendResult, StaffRef, TagTone } from '../types'

/** Staff-only note about a customer. Never sent to the platform (separate table on the server). */
export interface Note {
  id: string
  /** The chat it was written in (the timeline shows it there). */
  conversation_id: string
  /** The customer; null in LINE groups. The customer tab shows every note of this contact. */
  contact_id: string | null
  text: string
  at: string
  edited_at: string | null
  author: StaffRef
  /** The author, or a head/owner. */
  can_edit: boolean
}

export interface TagDef {
  id: string
  name: string
  tone: TagTone
  /** Lower = more important (the one chip a list row shows). */
  sort: number
  /** Customers carrying it. */
  count: number
}

export interface TagList {
  items: TagDef[]
  /** Head/owner: may add, rename, recolour, reorder, delete. Anyone may put tags on a customer. */
  can_manage: boolean
}

export interface FollowUpRule {
  ok: boolean
  /** How it would go out: standard (Messenger/IG ≤ 24 h) · human_agent (tag, ≤ 7 days) · push (LINE). */
  via: 'standard' | 'human_agent' | 'push' | 'web' | null
  /** Meta window end (24 h / 7 days after the customer's last message). */
  send_until: string | null
  /** Why it cannot be sent (Thai). */
  reason: string | null
  /** What staff should know (Thai), e.g. LINE push uses the monthly quota. */
  note: string | null
}

export interface FollowUpDraft {
  text: string
  /** canned = library answer tagged follow_up · ai = short AI draft · template = the team's default text */
  source: 'canned' | 'ai' | 'template'
  canned_id: string | null
  label: string
}

export interface FollowUpInfo {
  conversation_id: string
  due: boolean
  due_at: string | null
  /** Our last message (the customer has been silent since). */
  silent_since: string | null
  rule: FollowUpRule
  draft: FollowUpDraft | null
  auto: {
    allowed: boolean
    enabled: boolean
    sending: boolean
    /** Why the automatic follow-up leaves this chat to staff although staff may still send (Messenger/IG past
     * 24 h: Human Agent is for a person's reply only). Null otherwise; older servers leave it out. */
    reason?: string | null
  }
}

export interface FollowUpSendBody {
  text: string
  client_id: string
  source: 'canned' | 'ai' | 'template' | 'manual'
  canned_id: string | null
}

export interface AlertValues {
  /** Red "เกินเวลา" after this many minutes of waiting. */
  overdue_min: number
  telegram_on: boolean
  telegram_wait_min: number
  /** 0 = off. */
  telegram_queue_over: number
  follow_up_after_min: number
  follow_up_ai: boolean
  follow_up_template: string
  auto_allowed: boolean
  /** Owner only. */
  auto_send: boolean
  /** Owner only: Meta approved the Human Agent permission. */
  meta_human_agent: boolean
}

export type AlertKey = keyof AlertValues

export interface AlertSettings {
  values: AlertValues
  can_edit: Record<AlertKey, boolean>
  owner_only: AlertKey[]
  status: {
    telegram_ready: boolean
    send_mode: 'off' | 'allowlist' | 'on' | string
    line: { account: string; name: string; push: boolean }[]
    meta_accounts: number
    follow_up_window_days: number
    ai_available: boolean
  }
}

export type PresenceState = 'viewing' | 'typing' | 'left'

export interface PresenceItem {
  conversation_id: string
  staff: StaffRef
  state: PresenceState
  /** Seconds this entry lives unless repeated. */
  ttl: number
}

export type CollabEvent =
  | ({ type: 'presence' } & PresenceItem)
  | { type: 'presence.snapshot'; items: PresenceItem[] }
  | {
      type: 'note.changed'
      action: 'created' | 'updated' | 'deleted'
      note_id: string
      conversation_id: string
      contact_id: string | null
    }
  | { type: 'tags.changed' }
  | { type: 'settings.changed'; section: string }

export interface CollabApi {
  listNotes(convId: string): Promise<Note[]>
  addNote(convId: string, text: string): Promise<Note>
  editNote(noteId: string, text: string): Promise<Note>
  deleteNote(noteId: string): Promise<void>
  listTags(): Promise<TagList>
  createTag(name: string, tone: TagTone): Promise<TagDef>
  updateTag(tagId: string, patch: { name?: string; tone?: TagTone; sort?: number }): Promise<TagDef>
  deleteTag(tagId: string): Promise<void>
  orderTags(ids: string[]): Promise<TagList>
  setConversationTags(convId: string, tagIds: string[]): Promise<{ conversation: ConversationSummary }>
  getFollowUp(convId: string): Promise<FollowUpInfo>
  sendFollowUp(convId: string, body: FollowUpSendBody): Promise<SendResult>
  skipFollowUp(convId: string): Promise<{ conversation: ConversationSummary }>
  setFollowUpAuto(convId: string, enabled: boolean): Promise<{ enabled: boolean }>
  getAlertSettings(): Promise<AlertSettings>
  putAlertSettings(changes: Partial<AlertValues>): Promise<AlertSettings>
}

export function isCollabEvent(v: unknown): v is CollabEvent {
  if (typeof v !== 'object' || v === null) return false
  const t = (v as { type?: unknown }).type
  return (
    t === 'presence' ||
    t === 'presence.snapshot' ||
    t === 'note.changed' ||
    t === 'tags.changed' ||
    t === 'settings.changed'
  )
}
